/**
 * Low-level admin-API client. Writes go browser → `api.<env>.eliospay.app`
 * directly (no BFF), with the operator's Firebase ID token as a Bearer header.
 * The backend is the only security boundary: it re-validates `roles:['admin']`
 * + a non-empty `backendId`, executes through the domain service, and records
 * the operator as the event actor. We surface its response verbatim.
 */
import { env } from "@/config/env";

export class AdminApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "AdminApiError";
  }

  /** 5xx / RetryableError — transient, safe to retry. */
  get retryable(): boolean {
    return this.status >= 500;
  }

  get forbidden(): boolean {
    return this.status === 403;
  }

  /** Optimistic-concurrency conflict (stale `updatedAt`) or invalid state transition. */
  get conflict(): boolean {
    return this.status === 409;
  }
}

export interface AdminRequest {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  body?: unknown;
  token: string;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: AdminRequest["query"]): string {
  const url = new URL(env.apiBaseUrl + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export async function adminRequest<T = unknown>(req: AdminRequest): Promise<T> {
  let res: Response;
  try {
    res = await fetch(buildUrl(req.path, req.query), {
      method: req.method,
      signal: req.signal,
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${req.token}`,
        ...(req.body !== undefined ? { "Content-Type": "application/json" } : {}),
        Accept: "application/json",
      },
      body: req.body !== undefined ? JSON.stringify(req.body) : undefined,
    });
  } catch (e) {
    // Network / CORS failure — no HTTP status.
    throw new AdminApiError(
      e instanceof Error ? `Network error: ${e.message}` : "Network error",
      0,
      "network",
      null,
    );
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message = extractMessage(parsed) ?? `${req.method} ${req.path} failed (${res.status})`;
    const code = extractCode(parsed);
    throw new AdminApiError(message, res.status, code, parsed);
  }

  return parsed as T;
}

function extractMessage(body: unknown): string | null {
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    if (typeof b.message === "string") return b.message;
    if (Array.isArray(b.message)) return b.message.join("; ");
    if (typeof b.error === "string") return b.error;
  }
  if (typeof body === "string" && body.trim()) return body;
  return null;
}

function extractCode(body: unknown): string | null {
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    if (typeof b.errorCode === "string") return b.errorCode;
    if (typeof b.code === "string") return b.code;
  }
  return null;
}
